// Figures for the reconciliation report (PDF) and the Excel summary - one module,
// so both outputs always carry the same numbers.
//
// Two lines, each built so its parts add up to its total exactly (Rishabh,
// 2026-10-24):
//
//  1. REGISTER ON THE AUDIT DATE
//       register on audit date = found in audit + recovered via PV + missing
//                                + bought after its place was audited + out of scope
//     bridged from the register as loaded:
//       loaded - SOLD on audit date - created by this audit - bought after the audit date (not seen)
//
//  2. PHYSICALLY AUDITED (every AST scan + every PV item)
//       audited = AST scans + PV items, split by the action taken on each, and
//       bridged to line 1: which scans/items became a "found" / "recovered" register
//       asset, and why the rest did not (repeat scan, code not in register, ...).
//
// Every count is a partition of real rows - no residual is ever dropped. Anything
// that does not fit a named bucket lands in an explicit "other" bucket, which the
// report prints, and `register.check` / the bridge sums are verified by the caller.
//
// Places (location / sub-location) are as on the audit date - see placeOf in index.js.

const REGISTER_KEYS = ['found', 'pvlink', 'missing', 'boughtafter', 'oos']

// Action rows of line 2, in display order. Unknown action names are appended, never
// dropped; records with no action are their own row.
const ACTION_ORDER = ['RECONCILED', 'ATA_ARN', 'SOLD', 'UNSOLD', 'LINK_ASSET', 'NEW_ASSET', 'IGNORE', 'MISSING']
export const NO_ACTION = '__NONE__'

const idOf = (v) => (v && v._id) ? String(v._id) : (v ? String(v) : null)

const num = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export const buildReconSummary = ({
  allAssets, partition, astData, pvData, decisionByAsset, placeOf,
  locName, clientName, actLabel, statusLabelOf, statusOrder, createdIdsOf
}) => {
  const rows = partition.rows || []
  const real = rows.filter(r => !r.isPlaceholder)
  const placeholders = rows.filter(r => r.isPlaceholder)
  const soldOnDate = partition.soldOnDate || []
  const postAudit = partition.postAudit || []

  const statusById = new Map(real.map(r => [String(r._id), r.assetStatus]))
  const soldIds = new Set(soldOnDate.map(a => String(a._id)))
  const loadedIds = new Set((allAssets || []).map(a => String(a._id)))
  const createdIds = new Set(real.filter(r => r.assetStatus === 'newasset').map(r => String(r._id)))

  // Invoice value as the report states it. New assets created by Process were saved
  // with purchase rate 0 as a placeholder, so 0 on one of them means "no invoice on
  // record", not "worth nothing" (Rishabh, 2026-10-24). Any other stored value,
  // including 0 on an ordinary register asset, is reported as stored.
  const valueOf = (asset) => {
    if (!asset) return null
    const v = num(asset.invoiceValue)
    if (v === 0 && createdIds.has(String(asset._id))) return null
    return v
  }
  const priced = () => ({ count: 0, valued: 0, value: 0 })
  const addValue = (b, asset) => {
    b.count += 1
    const v = valueOf(asset)
    if (v != null) { b.valued += 1; b.value += v }
  }

  // ---------- line 1: register on the audit date ----------
  const reg = {}
  REGISTER_KEYS.forEach(k => { reg[k] = priced() })
  const outcome = { newasset: priced() }
  real.forEach(r => {
    if (reg[r.assetStatus]) addValue(reg[r.assetStatus], r)
    else if (r.assetStatus === 'newasset') addValue(outcome.newasset, r)
  })
  placeholders.forEach(r => addValue(outcome.newasset, r))
  const registerTotal = REGISTER_KEYS.reduce((n, k) => n + reg[k].count, 0)
  const createdInRegister = real.filter(r => r.assetStatus === 'newasset').length
  const otherStatus = real.filter(r => !reg[r.assetStatus] && r.assetStatus !== 'newasset').length
  const register = {
    loaded: (allAssets || []).length,
    soldOnDate: soldOnDate.length,
    createdByAudit: createdInRegister,
    afterAuditDate: postAudit.length,
    otherStatus,
    total: registerTotal,
    parts: reg,
    // loaded = SOLD + after-date + created + register-on-date (+ other, expected 0)
    check: (allAssets || []).length - soldOnDate.length - postAudit.length - createdInRegister - otherStatus - registerTotal
  }

  // ---------- the two headline lines of the PDF (Rishabh, 2026-10-27) ----------
  //  A. today:  register today (without SOLD) = register on the audit date (without
  //             marked unsold) + marked unsold + new assets created by the audit
  //             + bought after the audit date
  //  B. close:  assets on the last audit date incl. new = found + recovered via PV
  //             + missing + bought after place audit + out of scope + new assets + marked unsold
  // "Marked unsold" = found assets whose sale this audit reversed (a processed UNSOLD
  // scan), split out of "found" - so found + unsold here equals line 1's found.
  const unsoldIds = new Set()
    ; (astData || []).forEach(d => {
      const id = idOf(d.matchedAsset)
      if (id && d.isProcessed && d.actionTaken && d.actionTaken.name === 'UNSOLD') unsoldIds.add(id)
    })
  const close = { found: priced(), unsold: priced(), newasset: priced() }
  real.forEach(r => {
    if (r.assetStatus === 'found') addValue(close[unsoldIds.has(String(r._id)) ? 'unsold' : 'found'], r)
    else if (r.assetStatus === 'newasset') addValue(close.newasset, r)
  })
  const closeParts = {
    found: close.found, pvlink: reg.pvlink, missing: reg.missing, boughtafter: reg.boughtafter,
    oos: reg.oos, newasset: close.newasset, unsold: close.unsold
  }
  const today = {
    total: (allAssets || []).length - soldOnDate.length,
    // Marked unsold during the audit get their own term: the register on the audit
    // date is read without them (Rishabh, 2026-10-27).
    onAuditDate: registerTotal - close.unsold.count,
    unsold: close.unsold.count,
    created: createdInRegister,
    afterAuditDate: postAudit.length,
    otherStatus
  }
  // Bought after the audit date and not seen: outside the audit, valued for the outcome table.
  today.afterAudit = priced()
  postAudit.forEach(a => addValue(today.afterAudit, a))
  today.check = today.total - today.onAuditDate - today.unsold - today.created - today.afterAuditDate - otherStatus
  const closing = {
    total: registerTotal + createdInRegister,
    parts: closeParts,
    check: registerTotal + createdInRegister - Object.keys(closeParts).reduce((n, k) => n + closeParts[k].count, 0)
  }
  // Line 2's total comes from line 1: register on the audit date + marked unsold + new
  // assets (= line 1's total less bought after the audit date). Printed on page 1 so the
  // reader can see where it comes from; anything but 0 here is a warning on the PDF.
  closing.fromToday = today.onAuditDate + today.unsold + today.created
  closing.todayCheck = closing.total - closing.fromToday

  // ---------- line 2: physically audited, by action ----------
  // Each action row carries the invoice value of the register assets its records
  // refer to - the scanned asset, the asset a PV item was linked to, or the assets a
  // NEW_ASSET created - valued once per asset within the row. Looked up by exact id
  // in the loaded register; an id not in it is counted but not priced.
  const assetById = new Map((allAssets || []).map(a => [String(a._id), a]))
  const assetIdsOf = (d, src) => {
    const name = d.actionTaken && d.actionTaken.name
    if (name === 'NEW_ASSET') return d.isProcessed && createdIdsOf ? createdIdsOf(d) : []
    if (src === 'pv') return name === 'LINK_ASSET' && d.actionTaken.refRecordId ? [String(d.actionTaken.refRecordId)] : []
    const id = idOf(d.matchedAsset)
    return id ? [id] : []
  }
  const actionKey = (d) => (d.actionTaken && d.actionTaken.name) || NO_ACTION
  const actions = new Map()
  const bump = (d, src) => {
    const k = actionKey(d)
    if (!actions.has(k)) actions.set(k, { key: k, ast: 0, pv: 0, astProcessed: 0, pvProcessed: 0, ids: new Set() })
    const a = actions.get(k)
    a[src] += 1
    if (d.isProcessed) a[src + 'Processed'] += 1
    assetIdsOf(d, src).forEach(id => a.ids.add(id))
  }
  ; (astData || []).forEach(d => bump(d, 'ast'))
  ; (pvData || []).forEach(d => bump(d, 'pv'))
  const rank = (k) => k === NO_ACTION ? 1000 : (ACTION_ORDER.indexOf(k) >= 0 ? ACTION_ORDER.indexOf(k) : 500)
  const actionRows = [...actions.values()]
    .sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key))
    .map(({ ids, ...a }) => {
      const v = priced()
      ids.forEach(id => {
        const asset = assetById.get(id)
        v.count += 1
        const val = asset ? valueOf(asset) : null
        if (val != null) { v.valued += 1; v.value += val }
      })
      return { ...a, label: a.key === NO_ACTION ? 'No action yet' : actLabel(a.key), assets: v.count, valued: v.valued, value: v.value }
    })

  // ---------- bridge: AST scans -> register line ----------
  // A code scanned twice gives two rows matched to the same register asset; the
  // first counts that asset, later ones are repeat scans.
  const astB = { found: 0, repeat: 0, notInRegister: 0, soldOnDate: 0, notLoaded: 0, other: 0 }
  const seenAst = new Set()
    ; (astData || []).forEach(d => {
      const id = idOf(d.matchedAsset)
      if (!id) { astB.notInRegister += 1; return }
      if (seenAst.has(id)) { astB.repeat += 1; return }
      seenAst.add(id)
      if (statusById.get(id) === 'found') astB.found += 1
      else if (soldIds.has(id)) astB.soldOnDate += 1
      else if (!loadedIds.has(id)) astB.notLoaded += 1
      else astB.other += 1
    })

  // ---------- bridge: PV items -> register line ----------
  const pvB = {
    recovered: 0, linkedAstFound: 0, linkedAgain: 0, linkedCreated: 0, linkedSold: 0, linkedNotLoaded: 0,
    linkedOther: 0, linkPending: 0, newCreated: 0, newPending: 0, ignored: 0, undecided: 0, other: 0
  }
    ; (pvData || []).forEach(d => {
      const name = d.actionTaken && d.actionTaken.name
      if (!name) { pvB.undecided += 1; return }
      if (name === 'IGNORE') { pvB.ignored += 1; return }
      if (name === 'NEW_ASSET') { pvB[d.isProcessed ? 'newCreated' : 'newPending'] += 1; return }
      if (name !== 'LINK_ASSET') { pvB.other += 1; return }
      if (!d.isProcessed) { pvB.linkPending += 1; return }
      const t = d.actionTaken.refRecordId ? String(d.actionTaken.refRecordId) : null
      const st = t ? statusById.get(t) : null
      if (st === 'pvlink' && decisionByAsset.pv.get(t) === d) pvB.recovered += 1
      else if (st === 'pvlink') pvB.linkedAgain += 1
      else if (st === 'found') pvB.linkedAstFound += 1
      // Linked to an asset this audit created (another PV item's / a repeat scan's
      // new asset): not in the register on the audit date, so not "recovered".
      else if (st === 'newasset') pvB.linkedCreated += 1
      else if (t && soldIds.has(t)) pvB.linkedSold += 1
      else if (!t || !loadedIds.has(t)) pvB.linkedNotLoaded += 1
      else pvB.linkedOther += 1
    })

  const newAssets = {
    created: createdInRegister,
    fromPv: real.filter(r => r.assetStatus === 'newasset' && r.pvRef).length,
    fromAst: real.filter(r => r.assetStatus === 'newasset' && r.astCreatedRef).length,
    pending: placeholders.length
  }

  // ---------- reconcile status of the AST scans, with invoice value ----------
  // Valued once per register asset: every scan of a duplicated code carries the
  // same matched asset, so summing per scan would count it again.
  const byStatus = new Map()
    ; (astData || []).forEach(d => {
      const label = statusLabelOf(d)
      if (!byStatus.has(label)) byStatus.set(label, { label, key: d.reconcileStatus, scans: 0, ids: new Map() })
      const b = byStatus.get(label)
      b.scans += 1
      const id = idOf(d.matchedAsset)
      if (id && !b.ids.has(id)) b.ids.set(id, d.matchedAsset)
    })
  const statusRank = (b) => { const i = statusOrder(b.key); return i < 0 ? 999 : i }
  const statusRows = [...byStatus.values()]
    .sort((a, b) => statusRank(a) - statusRank(b) || b.scans - a.scans)
    .map(b => {
      const v = priced()
      b.ids.forEach(asset => addValue(v, asset))
      return { label: b.label, scans: b.scans, assets: b.ids.size, valued: v.valued, value: v.value }
    })

  // ---------- location -> sub-location on the audit date ----------
  const NO_SUB = '(no sub-location)'
  const blank = () => ({ assets: 0, found: 0, pvlink: 0, missing: 0, boughtafter: 0, oos: 0 })
  const add = (t, st) => { t.assets += 1; t[st] += 1 }
  const locs = new Map()
  const grand = blank()
  real.forEach(r => {
    if (!reg[r.assetStatus]) return
    const { loc, cli } = placeOf(r)
    if (!locs.has(loc)) locs.set(loc, { id: loc, totals: blank(), subs: new Map() })
    const L = locs.get(loc)
    if (!L.subs.has(cli)) L.subs.set(cli, { id: cli, totals: blank() })
    add(L.subs.get(cli).totals, r.assetStatus)
    add(L.totals, r.assetStatus)
    add(grand, r.assetStatus)
  })
  const nameLoc = (id) => id === 'NA' ? '(no location)' : (locName(id) || '(location not in master)')
  const nameSub = (id) => id === 'NA' ? NO_SUB : (clientName(id) || '(sub-location not in master)')
  const az = (a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
  const locationRows = [...locs.values()]
    .map(L => ({
      id: L.id, name: nameLoc(L.id), totals: L.totals,
      // A-Z, with "(no sub-location)" kept last under its location.
      subs: [...L.subs.values()].map(s => ({ id: s.id, name: nameSub(s.id), totals: s.totals }))
        .sort((a, b) => (a.id === 'NA') - (b.id === 'NA') || az(a, b))
    }))
    // A-Z, with "(no location)" last, the same rule as sub-locations.
    .sort((a, b) => (a.id === 'NA') - (b.id === 'NA') || az(a, b))

  return {
    register, today, closing, outcome: { ...reg, ...outcome }, actionRows,
    audit: { ast: (astData || []).length, pv: (pvData || []).length },
    astBridge: astB, pvBridge: pvB, newAssets, statusRows,
    locations: { rows: locationRows, total: grand }
  }
}

// The lines of each bridge, labelled, in print order. Zero lines are kept: an
// explicit 0 shows the case was checked.
export const astBridgeLines = (b) => [
  ['Register assets found (counted in line 1)', b.found],
  ['Repeat scans of a code already scanned', b.repeat],
  ['Code not in the register', b.notInRegister],
  ['Register asset in SOLD on the audit date', b.soldOnDate],
  ['Register asset not in this load', b.notLoaded],
  ['Other', b.other]
]
export const pvBridgeLines = (b) => [
  ['Linked, recovered a register asset (counted in line 1)', b.recovered],
  ['Linked to an asset AST already found', b.linkedAstFound],
  ['Linked to an asset another PV item already recovered', b.linkedAgain],
  ['Linked to a new asset this audit created', b.linkedCreated],
  ['Linked to an asset in SOLD on the audit date', b.linkedSold],
  ['Linked to an asset not in this load', b.linkedNotLoaded],
  ['Linked, other', b.linkedOther],
  ['Link not processed yet', b.linkPending],
  ['New asset created', b.newCreated],
  ['New asset not processed yet', b.newPending],
  ['Ignored', b.ignored],
  ['No action yet', b.undecided],
  ['Other action', b.other]
]
export const sumLines = (lines) => lines.reduce((n, l) => n + l[1], 0)

// ---------- Annexure: every physical record and what was done with it ----------
// The PDF's annexure (Rishabh, 2026-10-27): each AST scan and PV item with the asset
// it became, where and when the audit found it, and the document Process raised
// (PI for a new asset, ATA/ARN for a transfer, SI for a reversed sale) - the
// processed remark exactly as stored. Plus decisions made on register assets with
// no scan (Assets tab). Every record lands in exactly one section: unknown actions
// get their own "Other" section and unprocessed records a "Not processed" one.
const ANNEX_SECTIONS = [
  ['AST|IGNORE', 'Found in audit - no change (Ignore)'],
  ['AST|RECONCILED', 'Found in audit - reconciled'],
  ['AST|ATA_ARN', 'Found at another place - transferred (ATA/ARN)'],
  ['AST|UNSOLD', 'Found although marked sold - sale reversed'],
  ['AST|SOLD', 'Scanned and marked sold'],
  ['AST|NEW_ASSET', 'Repeat scan of a code - new asset created (PI)'],
  ['PV|LINK_ASSET', 'Item without a code - linked to a register asset'],
  ['PV|NEW_ASSET', 'Item without a code - new asset created (PI)'],
  ['PV|RECONCILED', 'Item without a code - reconciled'],
  ['PV|IGNORE', 'Item without a code - ignored']
]

// codeOfAsset(id) -> register asset code, or null when the id is not in the loaded register.
export const buildAnnexure = ({ astData, pvData, assetQueue, codeOfAsset, createdIdsOf, placeOf, locName, clientName }) => {
  const sections = new Map(ANNEX_SECTIONS.map(([key, title]) => [key, { key, title, rows: [] }]))
  const sectionFor = (source, action, processed) => {
    const key = !processed ? `${source}|PENDING` : `${source}|${action || 'NONE'}`
    if (!sections.has(key)) {
      const title = !processed ? `${source === 'ASSET' ? 'Register asset decisions' : source} - not processed yet`
        : source === 'ASSET' ? `Register asset (no scan) - ${action}` : `Other - ${source} ${action || 'no action'}`
      sections.set(key, { key, title, rows: [] })
    }
    return sections.get(key)
  }
  const codeOrFlag = (id) => codeOfAsset(id) || `id ${id} not in register`
  const createdCodes = (r) => {
    const ids = createdIdsOf(r)
    return ids.length ? ids.map(codeOrFlag).join(', ') : 'created asset not recorded'
  }

  ;(astData || []).forEach(r => {
    const action = r.actionTaken && r.actionTaken.name
    const m = r.matchedAsset || {}
    const asset = action === 'NEW_ASSET' && r.isProcessed ? createdCodes(r)
      : (m._id ? (m.assetCode || codeOrFlag(m._id)) : 'not in register')
    sectionFor('AST', action, r.isProcessed).rows.push({
      record: r.astNo || '', code: r.assetCode || '', asset,
      model: (m.assetModel && m.assetModel.name) || '',
      loc: locName(idOf(r.location)) || '', sub: clientName(idOf(r.client)) || '',
      time: r.time || null, reference: r.isProcessed ? (r.processedRemark || '') : (action || 'no decision')
    })
  })
  ;(pvData || []).forEach(r => {
    const action = r.actionTaken && r.actionTaken.name
    const asset = !r.isProcessed ? '' : action === 'NEW_ASSET' ? createdCodes(r)
      : action === 'LINK_ASSET' ? codeOrFlag(String(r.actionTaken.refRecordId)) : ''
    sectionFor('PV', action, r.isProcessed).rows.push({
      record: r.pvNo || '', code: r.tempAssetCode || '', asset,
      model: (r.assetModel && r.assetModel.name) || '',
      loc: locName(idOf(r.location)) || '', sub: clientName(idOf(r.client)) || '',
      time: r.time || null, reference: r.isProcessed ? (r.processedRemark || '') : (action || 'no decision')
    })
  })
  // Decisions on register assets themselves (Assets tab): no scan, so no audit date.
  ;(assetQueue || []).forEach(r => {
    const d = r.savedAssetDecision
    if (!d || !d.actionTaken || !d.actionTaken.name) return
    const p = placeOf(r)
    sectionFor('ASSET', d.actionTaken.name, d.isProcessed).rows.push({
      record: 'Assets tab', code: r.assetCode || '', asset: r.assetCode || '',
      model: (r.assetModel && r.assetModel.name) || '',
      loc: locName(p.loc === 'NA' ? null : p.loc) || '', sub: clientName(p.cli === 'NA' ? null : p.cli) || '',
      time: null, reference: d.isProcessed ? (d.processedRemark || '') : d.actionTaken.name
    })
  })

  // Read like a walk through the site: location, sub-location, then when it was found.
  const az = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })
  const t = (x) => (x ? new Date(x).getTime() : Infinity)
  const out = [...sections.values()].filter(s => s.rows.length)
  out.forEach(s => s.rows.sort((a, b) => az(a.loc, b.loc) || az(a.sub, b.sub) || t(a.time) - t(b.time) || az(a.code, b.code)))
  const scanned = out.filter(s => !s.key.startsWith('ASSET|')).reduce((n, s) => n + s.rows.length, 0)
  return { sections: out, scanned, audited: (astData || []).length + (pvData || []).length }
}

// Missing list for the PDF: highest invoice value first; assets with no invoice value
// on record last (never read as 0), then by asset code.
export const missingByValue = (rows) => [...(rows || [])].sort((a, b) => {
  const va = num(a.invoiceValue), vb = num(b.invoiceValue)
  if (va == null || vb == null) return (va == null) - (vb == null) || String(a.assetCode || '').localeCompare(String(b.assetCode || ''))
  return vb - va || String(a.assetCode || '').localeCompare(String(b.assetCode || ''))
})
